import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '../../shared/components/AppComponents';
import { apiFetchJson } from '../../apiClient';
import { useTheme } from '../../shared/ThemeContext';
import EmailPreferences from './EmailPreferences';
import './Profile.css';

function Profile({ user }) {
  const { instance } = useMsal();
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetchJson(instance, '/api/Users/profile');
      setProfile(data);
      setName(data.name);
    } catch (err) { console.error('Failed to load profile:', err); }
    setLoading(false);
  }, [instance]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const data = await apiFetchJson(instance, '/api/Users/profile', {
        method: 'PUT',
        body: JSON.stringify({ name: name.trim() })
      });
      setProfile(data);
      setEditing(false);
    } catch (err) { console.error('Failed to save profile:', err); }
    setSaving(false);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="profile-page">
      <AppHeader title={t('profile.title')} user={user} />

      {loading && <div className="profile-loading">{t('common.loading')}</div>}

      {profile && (
        <div className="profile-card">
          <div className="profile-avatar">
            {(profile.name || '?')[0].toUpperCase()}
          </div>

          <div className="profile-field">
            <label>{t('profile.displayName')}</label>
            {editing ? (
              <div className="profile-edit-row">
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="profile-input"
                  autoFocus
                  maxLength={100}
                />
                <button className="profile-btn primary" onClick={handleSave} disabled={saving || !name.trim()}>
                  {saving ? t('common.saving') : t('common.save')}
                </button>
                <button className="profile-btn secondary" onClick={() => { setName(profile.name); setEditing(false); }}>
                  {t('common.cancel')}
                </button>
              </div>
            ) : (
              <div className="profile-edit-row">
                <span className="profile-value">{profile.name}</span>
                <button className="profile-btn secondary" onClick={() => setEditing(true)}>✏️ {t('common.edit')}</button>
              </div>
            )}
          </div>

          <div className="profile-field">
            <label>{t('profile.email')}</label>
            <span className="profile-value muted">{profile.email}</span>
          </div>

          <div className="profile-field">
            <label>{t('profile.memberSince')}</label>
            <span className="profile-value muted">
              {new Date(profile.joinedAt).toLocaleDateString(i18n.language === 'no' ? 'nb-NO' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>

          <div className="profile-field">
            <label>{t('profile.theme')}</label>
            <div className="theme-selector">
              <button
                className={`theme-btn ${theme === 'light' ? 'active' : ''}`}
                onClick={() => theme !== 'light' && toggleTheme()}
              >
                ☀️ {t('profile.themeLight')}
              </button>
              <button
                className={`theme-btn ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => theme !== 'dark' && toggleTheme()}
              >
                🌙 {t('profile.themeDark')}
              </button>
            </div>
          </div>

          <div className="profile-field">
            <label>{t('profile.language')}</label>
            <div className="language-selector">
              <button
                className={`language-btn ${i18n.language === 'en' ? 'active' : ''}`}
                onClick={() => changeLanguage('en')}
              >
                🇬🇧 {t('profile.languageEnglish')}
              </button>
              <button
                className={`language-btn ${i18n.language === 'no' ? 'active' : ''}`}
                onClick={() => changeLanguage('no')}
              >
                🇳🇴 {t('profile.languageNorwegian')}
              </button>
            </div>
          </div>
        </div>
      )}

      {profile && <EmailPreferences />}
    </div>
  );
}

export default Profile;
